/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {IndexedObject} from '@dvrd/dvr-controls';
import {merge} from 'lodash';

export interface SystemOptions extends IndexedObject<any> {
    platformUrl: string;
    sentryUrl: string | null;
    appBaseUrl: string;
    gaID: string | null;
    mode: 'prod' | 'test' | 'dev';
    filePrefix?: string;
    testPremium: boolean;
    tinyApiKey: string;
    useLegacyEditor: boolean;
    allowExperimental: boolean;
    stripeApiKey: string;
    appUrl: string;
    maintenanceMode: boolean;
    bolOauthClientId: string;
    renderInvFromOtherAccount: boolean;
    hideProductSyncForUsers: Array<string>;
    countingReviewsFrom: string;
    showDevUpdates?: 'none' | 'all' | 'regular';
}

interface Settings extends IndexedObject<SystemOptions> {
    prod: SystemOptions;
    dev: SystemOptions;
    accept: SystemOptions;
}

const defaultSettings: SystemOptions = {
    mode: 'prod',
    platformUrl: '',
    sentryUrl: null,
    appBaseUrl: '',
    gaID: 'G-DLK9PDZ6EY',
    testPremium: true,
    tinyApiKey: '0pdhayykjtfwu6s03da4co7s0o4jxohguk6wq2cxgs6k6mh6',
    useLegacyEditor: false,
    allowExperimental: false,
    stripeApiKey: '',
    appUrl: '',
    maintenanceMode: false,
    bolOauthClientId: 'e2f76440-cd8f-4c62-98ad-acfbc1a7cb8b',
    renderInvFromOtherAccount: true,
    hideProductSyncForUsers: ['2abaebcf-942e-4b6c-880a-145c1ae0a6b0', 'b14032d9-5bae-4f8b-95cc-dc5665f0aa38'],
    countingReviewsFrom: '2023-08-17',
};

const appSettings: Settings = {
    prod: {
        ...defaultSettings,
        mode: 'prod',
        platformUrl: 'https://app.bolmate.nl/bolmate-core/v1/',
        gaID: 'G-DLK9PDZ6EY',
        filePrefix: 'upload/',
        useLegacyEditor: false,
        appUrl: 'https://app.bolmate.nl',
        maintenanceMode: false,
        stripeApiKey: 'pk_live_51Hj5gkJJllIUmlpmGNi7jzYiZosHz5I13UXxRkNop14URxcTrrITAhH7OnRx3lPWBVgQzmQ66ywm4QVDUQJJoJgH00oRVqIjmU',
        sentryUrl: 'https://d032b45f225de0d9d0e6563c362acf20@o437233.ingest.sentry.io/4506546501844992',
    },
    accept: {
        ...defaultSettings,
        mode: 'test',
        platformUrl: 'https://staging.bolmate.nl/bolmate-core/v1/',
        filePrefix: 'upload/',
        sentryUrl: 'https://55421af56e0747e5b2efcf8a40384b71@o437233.ingest.sentry.io/5399518',
        testPremium: false,
        useLegacyEditor: false,
        allowExperimental: false,
        maintenanceMode: false,
        stripeApiKey: 'pk_test_51Hj5gkJJllIUmlpmVmxTs0u20lppRYx4p9MniVyb8BZBIKDJ1umKOEQyw17XJYTZbqz19dVoyn74Ygg3CY01PnDj00zPsEt8z6',
        appUrl: 'https://staging.bolmate.nl',
        countingReviewsFrom: '2023-08-16'
    },
    dev: {
        ...defaultSettings,
        mode: 'dev',
        platformUrl: 'http://127.0.0.1:30377/v1/',
        filePrefix: 'https://app.bolmate.nl/upload/',
        useLegacyEditor: false,
        allowExperimental: true,
        stripeApiKey: 'pk_test_51Hj5gkJJllIUmlpmVmxTs0u20lppRYx4p9MniVyb8BZBIKDJ1umKOEQyw17XJYTZbqz19dVoyn74Ygg3CY01PnDj00zPsEt8z6',
        appUrl: 'http://127.0.0.1:1337',
        maintenanceMode: false,
        showDevUpdates: 'regular',
    }
};

const mode = window.mode || 'prod';
window.allowCookies = true
const windowSettings = window.settings ?? {};
window.settings = merge({}, appSettings[mode], windowSettings);
